import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTierPricing } from '../config/pricing';

// Prices come from config/pricing.js; only the accent colour is local, since
// it is presentation and nothing computes against it.
const TIER_COLORS = {
  starter: '#3b82f6',
  pro: '#8b5cf6',
  enterprise: '#f59e0b'
};

const TIERS = Object.fromEntries(
  Object.keys(TIER_COLORS).map(key => {
    const tier = getTierPricing(key);
    return [key, {
      name: tier.name,
      monthlyPrice: tier.monthlyPrice,
      setupFee: tier.setupFee,
      color: TIER_COLORS[key]
    }];
  })
);

function SignupPayment() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const submitLockRef = useRef(false);
  const [error, setError] = useState('');
  const [selectedTier, setSelectedTier] = useState(null);
  const [squareLoaded, setSquareLoaded] = useState(false);
  const [card, setCard] = useState(null);
  const [autoBilling, setAutoBilling] = useState(true);
  const cardContainerRef = useRef(null);
  const paymentsRef = useRef(null);

  // Get Square configuration
  const SQUARE_APP_ID = process.env.REACT_APP_SQUARE_APPLICATION_ID;
  const SQUARE_LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID;
  const isProduction = process.env.REACT_APP_SQUARE_ENVIRONMENT === 'production';

  // Load Square Web Payments SDK
  useEffect(() => {
    const loadSquareSDK = async () => {
      // Check if already loaded
      if (window.Square) {
        setSquareLoaded(true);
        return;
      }

      // Load the SDK script
      const script = document.createElement('script');
      script.src = isProduction
        ? 'https://web.squarecdn.com/v1/square.js'
        : 'https://sandbox.web.squarecdn.com/v1/square.js';
      script.async = true;

      script.onload = () => {
        setSquareLoaded(true);
      };

      script.onerror = () => {
        setError('Failed to load payment system. Please refresh and try again.');
      };

      document.body.appendChild(script);
    };

    loadSquareSDK();
  }, [isProduction]);

  // Check auth and get selected tier
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    const tier = sessionStorage.getItem('selectedTier');
    if (!tier || !TIERS[tier]) {
      navigate('/signup/select-tier');
      return;
    }

    setSelectedTier(TIERS[tier]);
    setSelectedTier(prev => ({ ...prev, key: tier }));
    setLoading(false);
  }, [navigate]);

  // Initialize Square Card when SDK is loaded
  const initializeCard = useCallback(async () => {
    if (!squareLoaded || !window.Square || !cardContainerRef.current || card) {
      return;
    }

    try {
      const payments = await window.Square.payments(SQUARE_APP_ID, SQUARE_LOCATION_ID);
      paymentsRef.current = payments;

      const cardInstance = await payments.card();
      await cardInstance.attach(cardContainerRef.current);
      setCard(cardInstance);
    } catch (err) {
      console.error('Failed to initialize Square card:', err);
      setError('Failed to initialize payment form. Please refresh and try again.');
    }
  }, [squareLoaded, card, SQUARE_APP_ID, SQUARE_LOCATION_ID]);

  useEffect(() => {
    if (squareLoaded && !loading && !card) {
      initializeCard();
    }
  }, [squareLoaded, loading, card, initializeCard]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Synchronous double-submit guard. `processing` only disables the button on
    // the next render, so two clicks dispatched before React re-renders would
    // both get through. A ref flips immediately.
    if (submitLockRef.current) return;
    submitLockRef.current = true;

    setError('');
    setProcessing(true);

    if (!card) {
      setError('Payment form not ready. Please wait and try again.');
      setProcessing(false);
      submitLockRef.current = false;
      return;
    }

    try {
      // Tokenize the card
      const result = await card.tokenize();

      if (result.status === 'OK') {
        const token = localStorage.getItem('adminToken');

        // Call backend to create subscription
        const response = await fetch(`${process.env.REACT_APP_API_URL}/billing/create-subscription`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            tier: selectedTier.key,
            cardNonce: result.token,
            autoBilling: autoBilling
          })
        });

        const data = await response.json();

        if (response.ok && data.success) {
          // Clear session storage
          sessionStorage.removeItem('selectedTier');

          // Redirect to dashboard with success message
          navigate('/admin/dashboard', {
            state: {
              message: `Welcome! You're now on the ${selectedTier.name} plan.`,
              type: 'success'
            }
          });
        } else {
          setError(data.error || 'Payment failed. Please try again.');
        }
      } else {
        // Handle tokenization errors
        let errorMessage = 'Card validation failed. ';
        if (result.errors) {
          errorMessage += result.errors.map(e => e.message).join(', ');
        }
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError('Payment failed. Please check your card details and try again.');
    } finally {
      setProcessing(false);
      submitLockRef.current = false;
    }
  };

  const handleBack = () => {
    navigate('/signup/select-tier');
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, var(--color-background-alt) 0%, var(--color-border) 100%)'
      }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  const tier = selectedTier;
  const totalToday = tier.monthlyPrice + tier.setupFee;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, var(--color-background-alt) 0%, var(--color-border) 100%)',
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Back Button */}
        <button
          onClick={handleBack}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--color-text-light)',
            fontSize: '16px',
            cursor: 'pointer',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <span style={{ fontSize: '20px' }}>←</span> Back to Plans
        </button>

        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth > 768 ? '1fr 1fr' : '1fr',
          gap: '30px'
        }}>
          {/* Order Summary */}
          <div style={{
            background: 'var(--color-surface)',
            borderRadius: '20px',
            padding: '35px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
            height: 'fit-content'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: 'var(--color-text)',
              marginBottom: '25px'
            }}>
              Order Summary
            </h2>

            <div style={{
              background: `${tier.color}15`,
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '25px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <h3 style={{
                    color: tier.color,
                    fontSize: '22px',
                    fontWeight: '700',
                    margin: 0
                  }}>
                    {tier.name} Plan
                  </h3>
                  <p style={{ color: 'var(--color-text-light)', margin: '5px 0 0 0', fontSize: '14px' }}>
                    Monthly subscription
                  </p>
                </div>
                <span style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: 'var(--color-text)'
                }}>
                  ${tier.monthlyPrice}
                </span>
              </div>
            </div>

            <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '20px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '12px',
                color: 'var(--color-text-light)'
              }}>
                <span>Monthly subscription</span>
                <span>${tier.monthlyPrice.toFixed(2)}</span>
              </div>

              {tier.setupFee > 0 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '12px',
                  color: 'var(--color-text-light)'
                }}>
                  <span>One-time setup fee</span>
                  <span>${tier.setupFee.toFixed(2)}</span>
                </div>
              )}

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '20px',
                paddingTop: '20px',
                borderTop: '2px solid var(--color-border)',
                fontSize: '18px',
                fontWeight: '700'
              }}>
                <span>Total due today</span>
                <span style={{ color: tier.color }}>${totalToday.toFixed(2)}</span>
              </div>

              <p style={{
                fontSize: '13px',
                color: 'var(--color-text-muted)',
                marginTop: '15px'
              }}>
                Your card will be charged ${tier.monthlyPrice}/month starting next month.
              </p>
            </div>
          </div>

          {/* Payment Form */}
          <div style={{
            background: 'var(--color-surface)',
            borderRadius: '20px',
            padding: '35px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.08)'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: 'var(--color-text)',
              marginBottom: '25px'
            }}>
              Payment Details
            </h2>

            <form onSubmit={handleSubmit}>
              {/* Square Card Element Container */}
              <div style={{ marginBottom: '25px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'var(--color-text)',
                  marginBottom: '10px'
                }}>
                  Card Information
                </label>
                <div
                  ref={cardContainerRef}
                  style={{
                    minHeight: '50px',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    padding: '12px',
                    background: 'var(--color-surface-sunken)'
                  }}
                >
                  {!squareLoaded && (
                    <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>Loading payment form...</p>
                  )}
                </div>
              </div>

              {/* Auto-billing Option */}
              <div style={{ marginBottom: '25px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  cursor: 'pointer',
                  padding: '15px',
                  background: autoBilling ? '#f0fdf4' : '#fafafa',
                  borderRadius: '10px',
                  border: `2px solid ${autoBilling ? '#22c55e' : '#e5e7eb'}`,
                  transition: 'all 0.2s ease'
                }}>
                  <input
                    type="checkbox"
                    checked={autoBilling}
                    onChange={(e) => setAutoBilling(e.target.checked)}
                    style={{
                      width: '20px',
                      height: '20px',
                      marginTop: '2px',
                      accentColor: '#22c55e',
                      cursor: 'pointer'
                    }}
                  />
                  <div>
                    <span style={{
                      fontWeight: '600',
                      color: 'var(--color-text)',
                      fontSize: '15px'
                    }}>
                      Enable automatic monthly billing
                    </span>
                    <p style={{
                      margin: '6px 0 0 0',
                      fontSize: '13px',
                      color: 'var(--color-text-light)',
                      lineHeight: '1.4'
                    }}>
                      {autoBilling
                        ? "Your card will be securely saved and automatically charged each month."
                        : "You'll receive an email reminder to pay manually each month."}
                    </p>
                  </div>
                </label>
              </div>

              {/* Disclosure Text */}
              <div style={{
                padding: '12px 15px',
                background: 'var(--color-surface-sunken)',
                borderRadius: '8px',
                marginBottom: '20px',
                fontSize: '12px',
                color: 'var(--color-text-light)',
                lineHeight: '1.5'
              }}>
                {autoBilling ? (
                  <>
                    By completing this purchase, you authorize DeedStack™ to charge your card <strong>${tier.monthlyPrice}/month</strong> until you cancel.
                  </>
                ) : (
                  <>
                    You'll receive a monthly invoice. Payment is due within 5 days of invoice date.
                  </>
                )}
              </div>

              {error && (
                <div style={{
                  background: 'var(--color-error-bg)',
                  color: 'var(--color-error-text)',
                  padding: '12px 15px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  fontSize: '14px'
                }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={processing || !card}
                style={{
                  width: '100%',
                  padding: '18px',
                  borderRadius: '12px',
                  border: 'none',
                  background: processing || !card
                    ? '#ccc'
                    : `linear-gradient(135deg, ${tier.color}, ${tier.color}dd)`,
                  color: 'white',
                  fontSize: '18px',
                  fontWeight: '700',
                  cursor: processing || !card ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                {processing ? 'Processing...' : `Pay $${totalToday.toFixed(2)}`}
              </button>

              <div style={{
                marginTop: '25px',
                textAlign: 'center'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  color: 'var(--color-text-muted)',
                  fontSize: '13px'
                }}>
                  <span style={{ fontSize: '18px' }}>🔒</span>
                  Secured by Square
                </div>
                <p style={{
                  fontSize: '12px',
                  color: 'var(--color-text-muted)',
                  marginTop: '10px'
                }}>
                  Your payment information is encrypted and secure.
                  <br />
                  We never store your full card number.
                </p>
              </div>
            </form>
          </div>
        </div>

        {/* Trust badges */}
        <div style={{
          textAlign: 'center',
          marginTop: '40px',
          color: 'var(--color-text-muted)',
          fontSize: '14px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '30px',
            marginBottom: '15px'
          }}>
            <span>✓ 30-day money-back guarantee</span>
            <span>✓ Cancel anytime</span>
            <span>✓ Secure checkout</span>
          </div>
          <p>
            Questions?{' '}
            <a href="https://deedstack.com/contact" style={{ color: 'var(--color-brand-text)' }}>
              Contact us
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default SignupPayment;
